import { Fragment, useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left_2.svg';
import chip from '~/assets/icons/chip.svg';
import star from '~/assets/icons/star.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { SearchInput } from '~/components/ui/SearchInput';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { IHandlingTuningPreset, InputAction } from '~/shared';
import { useModal } from '../../hooks';
import {
	setActiveStage,
	setMenuTabsLocked,
	setSaveAbsoluteIndex,
	setSaveFavoriteMaxIndex,
	setSaveNonFavoriteMaxIndex,
	setSavePresetList,
	setSaveSelectedIndexNext,
	setSaveSelectedIndexPrev,
	setSaveSelectedList,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Save } from '../Save';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledEmptyContainer,
	StyledHeading,
	StyledSavedListContainer,
	StyledSearchContainer,
} from './SavedTab.styled';

const SavedTab = () => {
	const dispatch = useLayoutDispatch();
	const saveSelectedIndex = useLayoutSelector(
		(state) => state.handlingList.saveSelectedIndex,
	);
	const saveSelectedList = useLayoutSelector(
		(state) => state.handlingList.saveSelectedList,
	);
	const saveAbsoluteIndex = useLayoutSelector(
		(state) => state.handlingList.saveAbsoluteIndex,
	);

	const modalState = useObservable(rage.modal.getCurrentModal());
	const presetList = useObservable(rage.handlingTuning.getPresets());
	const [favoriteList, setFavoriteList] = useState<IHandlingTuningPreset[]>([]);
	const [nonFavoriteList, setNonFavoriteList] = useState<
		IHandlingTuningPreset[]
	>([]);
	const [scrollOffset, setScrollOffset] = useState<number>(0);
	const [search, setSearch] = useState<string>('');

	const { handleSetupSaveModal, handleSetupNewPresetModal } = useModal();

	useInputAction(InputAction.CommonMenuNavUp, () => {
		if (modalState) {
			return;
		}

		dispatch(setSaveSelectedIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavDown, () => {
		if (modalState) {
			return;
		}

		dispatch(setSaveSelectedIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, handleSetupSaveModal);

	useInputAction(InputAction.HandlingSetFavorite, () => {
		if (modalState) {
			return;
		}

		if (presetList.length !== 0) {
			rage.handlingTuning
				.setPresetFavorite(
					saveAbsoluteIndex,
					!presetList[saveAbsoluteIndex].favorite,
				)
				.catch((err) => rage.notifications.showException(err));
		}
	});

	const handleScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
		const scrollTop = (e as any).target?.scrollTop;
		if (scrollTop) {
			setScrollOffset(scrollTop);
		}
	}, []);

	const handlePreventScroll = useCallback((ev: KeyboardEvent) => {
		const { key } = ev;

		if (key === 'ArrowUp' || key === 'ArrowDown') {
			ev.preventDefault();
		}
	}, []);

	useEffect(() => {
		dispatch(setSavePresetList(presetList));
	}, [presetList]);

	useEffect(() => {
		setFavoriteList(
			presetList.filter((item) => item?.favorite && item.name.includes(search)),
		);
		setNonFavoriteList(
			presetList.filter(
				(item) => !item?.favorite && item.name.includes(search),
			),
		);
	}, [presetList, search]);

	useEffect(() => {
		dispatch(setSaveFavoriteMaxIndex(favoriteList.length - 1));
		dispatch(setSaveNonFavoriteMaxIndex(nonFavoriteList.length - 1));
		if (favoriteList.length === 0) {
			dispatch(setSaveSelectedList('nonFavorite'));
		}
	}, [favoriteList, nonFavoriteList]);

	useEffect(() => {
		const saveDiv = document.getElementById(
			`save-${saveSelectedList}-${saveSelectedIndex}`,
		);
		if (saveDiv !== null) {
			setTimeout(() => {
				saveDiv.scrollIntoView({ block: 'nearest' });
			}, 50);
		}
		if (saveSelectedList === 'favorite' && favoriteList.length > 0) {
			dispatch(
				setSaveAbsoluteIndex(
					presetList.indexOf(favoriteList[saveSelectedIndex]),
				),
			);
		} else if (
			saveSelectedList === 'nonFavorite' &&
			nonFavoriteList.length > 0
		) {
			dispatch(
				setSaveAbsoluteIndex(
					presetList.indexOf(nonFavoriteList[saveSelectedIndex]),
				),
			);
		}
	}, [saveSelectedIndex, saveSelectedList, presetList]);

	useEffectOnce(() => {
		window.addEventListener('keydown', handlePreventScroll);

		return () => window.removeEventListener('keydown', handlePreventScroll);
	});

	return (
		<div>
			<StyledSearchContainer>
				<SearchInput
					placeholder={'Введите название для поиска'}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					active={!!search.trim()}
					onFocus={() => dispatch(setMenuTabsLocked(true))}
					onBlur={() => dispatch(setMenuTabsLocked(false))}
				/>
			</StyledSearchContainer>
			{presetList.length === 0 ? (
				<StyledEmptyContainer>
					<Text>
						К сожалению, у вас нет
						<br />
						сохраненных настроек ТС
					</Text>
				</StyledEmptyContainer>
			) : (
				<ScrollContainer
					onScroll={handleScroll}
					width={380}
					height={463}
					style={{ marginTop: 30 }}
					showMask
				>
					{favoriteList.length > 0 && (
						<Fragment>
							<StyledHeading>
								<img src={star} />
								<Text>Избранное</Text>
							</StyledHeading>
							<StyledSavedListContainer>
								{favoriteList.map((item, index) => (
									<Save
										name={item.name}
										mode={item.mode}
										info={item.info}
										author={item.author}
										creationDate={item.creationDate}
										isFavorite={item.favorite}
										list={'favorite'}
										key={index}
										index={index}
										absoluteIndex={presetList.indexOf(item)}
										selected={
											saveSelectedList === 'favorite' &&
											index === saveSelectedIndex
										}
										id={`save-favorite-${index}`}
										scrollOffset={scrollOffset}
									/>
								))}
							</StyledSavedListContainer>
						</Fragment>
					)}
					{nonFavoriteList.length > 0 && (
						<Fragment>
							<StyledHeading
								style={{ marginTop: favoriteList.length > 0 ? 30 : 0 }}
							>
								<img src={chip} />
								<Text>Все настройки</Text>
							</StyledHeading>
							<StyledSavedListContainer>
								{nonFavoriteList.map((item, index) => (
									<Save
										name={item.name}
										mode={item.mode}
										info={item.info}
										author={item.author}
										creationDate={item.creationDate}
										isFavorite={item?.favorite}
										list={'nonFavorite'}
										key={index}
										index={index}
										absoluteIndex={presetList.indexOf(item)}
										selected={
											saveSelectedList === 'nonFavorite' &&
											index === saveSelectedIndex
										}
										id={`save-nonFavorite-${index}`}
										scrollOffset={scrollOffset}
									/>
								))}
							</StyledSavedListContainer>
						</Fragment>
					)}
				</ScrollContainer>
			)}
			<StyledButtonsContainer>
				<Button
					height={ButtonHeight.Height50}
					color={ButtonColors.Default}
					style={{ fontSize: 16 }}
					onClick={() => dispatch(setActiveStage(StageTypes.Category))}
				>
					<StyledButtonContent>
						<img src={arrowLeft} />
						<Text>Вернуться</Text>
					</StyledButtonContent>
				</Button>
				<Button
					height={ButtonHeight.Height50}
					color={ButtonColors.Select}
					style={{ fontSize: 16 }}
					onClick={handleSetupNewPresetModal}
				>
					<Text>Сохранить</Text>
				</Button>
			</StyledButtonsContainer>
		</div>
	);
};

export { SavedTab };
